import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    Brain,
    Code,
    Database,
    Briefcase,
    Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import FAQ from "../components/FAQ";
import { PLACEMENT_TRACKS_PAGE } from "../data/home-data";
import { PLACEMENT_FAQS } from "../data/faq-data";

const ICON_MAP = {
    Brain,
    Code,
    Database,
    Briefcase,
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.5, ease: [0.23, 1, 0.32, 1] },
};

const PLACEMENT_TRACKS = PLACEMENT_TRACKS_PAGE.tracks;

export default function PlacementTracksPage() {
    const navigate = useNavigate();

    return (
        <main className="pt-24" data-testid="placement-tracks-page">
            {/* Hero */}
            <section className="py-16 bg-gradient-to-b from-aipeach/30 via-white to-aisky/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <motion.div {...fadeUp} className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 bg-aiorange/10 text-aiorange text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-4">
                            <Sparkles className="h-3.5 w-3.5" />{" "}
                            {PLACEMENT_TRACKS_PAGE.hero.badge}
                        </span>
                        <h1
                            className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[1.05]"
                            dangerouslySetInnerHTML={{
                                __html: PLACEMENT_TRACKS_PAGE.hero.headline,
                            }}
                        />
                        <p className="mt-5 text-lg text-gray-600 max-w-2xl leading-relaxed">
                            {PLACEMENT_TRACKS_PAGE.hero.description}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Tracks */}
            <section className="pb-20 bg-gradient-to-b from-aisky/20 via-white to-[#FFF5F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <div className="space-y-8">
                        {PLACEMENT_TRACKS_PAGE.tracks.map((track, i) => {
                            const isReversed = i % 2 === 1;
                            return (
                                <motion.div
                                    key={track.key}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{
                                        delay: i * 0.1,
                                        duration: 0.5,
                                    }}
                                    className={`group relative rounded-2xl overflow-hidden border shadow-sm hover:shadow-lg transition-all ${track.border} bg-gradient-to-br ${track.gradient}`}
                                >
                                    <div
                                        className={`flex flex-col md:flex-row ${isReversed ? "md:flex-row-reverse" : ""}`}
                                    >
                                        {/* Image side — compact */}
                                        <div className="md:w-72 shrink-0 h-56 md:h-auto relative overflow-hidden">
                                            <img
                                                src={track.image}
                                                alt={track.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                                            {/* Track number badge */}
                                            <div
                                                className={`absolute top-4 left-4 w-10 h-10 rounded-full ${track.accent} flex items-center justify-center text-white font-display font-bold text-lg shadow-lg`}
                                            >
                                                {i + 1}
                                            </div>
                                            <div className="absolute bottom-4 left-4 right-4">
                                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                                                    {track.badge}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Content side — compact */}
                                        <div className="flex-1 p-6 sm:p-8 flex flex-col justify-center">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div>
                                                    <h3 className="font-display font-black text-2xl text-gray-900">
                                                        {track.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 font-medium">
                                                        {track.tagline}
                                                    </p>
                                                </div>
                                            </div>

                                            <ul className="space-y-2 mb-5">
                                                {track.highlights.map(
                                                    (h, hi) => (
                                                        <li
                                                            key={hi}
                                                            className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed"
                                                        >
                                                            <div
                                                                className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${track.accent}`}
                                                            />
                                                            {h}
                                                        </li>
                                                    ),
                                                )}
                                            </ul>

                                            <div>
                                                <Button
                                                    onClick={() =>
                                                        navigate(
                                                            "/waitlist?role=student",
                                                        )
                                                    }
                                                    className={`${track.accent} hover:opacity-90 text-white rounded-full px-6 py-4 font-semibold text-sm shadow-md`}
                                                >
                                                    {track.cta}{" "}
                                                    <ArrowRight className="ml-1.5 h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FAQ
                tag={PLACEMENT_TRACKS_PAGE.faq.tag}
                headline={PLACEMENT_TRACKS_PAGE.faq.headline}
                items={PLACEMENT_FAQS}
                tabs={true}
            />
        </main>
    );
}
